import React, { useState } from 'react';
import { X } from 'lucide-react';
import { ImageIcon } from 'lucide-react';
import TankCanvasEditor from './TankCanvasEditor';

/**
 * TankCanvasModal
 *
 * Props:
 *  isOpen       - boolean
 *  onClose      - () => void
 *  imageType    - 'type1' | 'type2'
 *  onSave       - (savedData) => void
 *  initialData  - optional pre-existing marks data
 *  existingImage - optional existing canvas data (marks + imageData)
 *  onhireId     - optional onhire ID used to fetch the image from the DB
 */
const TankCanvasModal = ({
  isOpen,
  onClose,
  imageType = 'type1',
  onSave,
  initialData = null,
  existingImage = null,
  onhireId = null,
}) => {
  const [isSaving, setIsSaving] = useState(false);

  if (!isOpen) return null;

  const handleSaveCanvas = (data) => {
    setIsSaving(true);
    try {
      const savedData = {
        ...data,
        type: imageType,
        savedAt: new Date().toISOString()
      };
      
      // Call parent callback - parent is responsible for storing the data
      if (onSave) {
        onSave(savedData);
      }

      setTimeout(() => {
        setIsSaving(false);
        onClose();
      }, 500);
    } catch (error) {
      console.error('Error saving canvas:', error);
      alert('Error saving canvas data');
      setIsSaving(false);
    }
  };

  const modalTitle = imageType === 'type1' 
    ? 'Tank Views - Front & Rear' 
    : 'Tank Sections - Front & Rear Grid';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-[1000] flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[100vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gray-100 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg">
              <ImageIcon className="w-6 h-6 text-tertiary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-tertiary">Tank Inspection Canvas Editor</h2>
              <p className="text-sm opacity-90 text-tertiary">{modalTitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors disabled:opacity-50"
            disabled={isSaving}
            title="Close"
          >
            <X className="w-6 h-6 text-tertiary" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(95vh-80px)]">
          <div className="p-6">
            {isSaving ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600 font-medium">Saving canvas...</p>
                </div>
              </div>
            ) : (
              <TankCanvasEditor
                imageType={imageType}
                onSave={handleSaveCanvas}
                onBack={onClose}
                initialData={initialData}
                existingImage={existingImage}
                onhireId={onhireId}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TankCanvasModal;